import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Plus, X, BookOpen, Edit2 } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// API Endpoint for DLA Fellowship
const API_BASE_URL = "https://backend.dlapakistan.com/api/dla-fellowship";

const DLAFellowship = () => {
  // Logic same as Aboutdla: Local storage fallback for reliability
  const [aboutContents, setAboutContents] = useState(() => {
    const savedData = localStorage.getItem("dlaFellowship_Storage");
    return savedData ? JSON.parse(savedData) : [];
  });

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0); 

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    is_active: true
  });

  const editorRef = useRef(null);
  const contentIdRef = useRef(null);
  const formRef = useRef(form); 
  const emerald600 = "#059669";

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Jodit Editor Configuration (Matching Aboutdla setup)
  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
    events: {
      afterInit: function(editor) {
        editorRef.current = editor;
        if (formRef.current.description && !editor.value) {
          editor.value = formRef.current.description;
        }
      }
    }
  }), []);

  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("access_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Fetch logic matching Aboutdla (with ID tracking and Storage)
  const fetchContents = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      
      if (response.data && response.data.data) {
        const data = response.data.data;
        // Backend usually returns a single object for this endpoint
        localStorage.setItem("dlaFellowship_Storage", JSON.stringify([data]));
        setAboutContents([data]);
        contentIdRef.current = data.id || data._id || data.pk;
      }
    } catch (error) {
      console.error("Fetch error:", error);
      const savedData = localStorage.getItem("dlaFellowship_Storage");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setAboutContents(parsedData);
        if (parsedData.length > 0) contentIdRef.current = parsedData[0].id;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setForm({ title: "", subtitle: "", description: "", is_active: true });
    setEditorKey(prev => prev + 1);
  };

  const handleEditorChange = useCallback((newContent) => {
    setForm(prev => ({ ...prev, description: newContent }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.subtitle.trim() || !form.description.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill in all fields.", confirmButtonColor: emerald600 });
      return;
    }

    try {
      setSaving(true);
      const dataToSend = { ...form, title: form.title.trim(), subtitle: form.subtitle.trim(), description: form.description.trim() };
      let response;

      if (aboutContents.length > 0) {
        // UPDATE Logic like Aboutdla
        response = await api.put(`${API_BASE_URL}/`, dataToSend);
        await Swal.fire({ icon: "success", title: "Updated!", text: "Fellowship content updated.", showConfirmButton: false, timer: 1500 });
      } else {
        // CREATE Logic
        response = await api.post(`${API_BASE_URL}/`, dataToSend);
        await Swal.fire({ icon: "success", title: "Created!", text: "Fellowship content saved.", showConfirmButton: false, timer: 1500 });
      }

      fetchContents();
      closeModal();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Save Failed", text: error.response?.data?.message || "An error occurred." });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (aboutContents.length > 0) {
      const content = aboutContents[0];
      setForm({
        title: content.title || "",
        subtitle: content.subtitle || "",
        description: content.description || "",
        is_active: true
      });
      setShowModal(true);
    }
  };

  if (loading) return <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">DLA Fellowship</h1>
            <p className="text-gray-600 mt-1">Manage Fellowship Page content</p>
          </div>
          <button
            onClick={aboutContents.length === 0 ? () => setShowModal(true) : handleEdit}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {aboutContents.length === 0 ? <><Plus className="w-5 h-5 mr-2" /> Add</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>}
          </button>
        </div>
      </div>

      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {aboutContents.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No content added yet.</p>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">{aboutContents[0]?.title}</h2>
            </div>
            <div className="w-full mb-10">
              <p className="text-gray-700 text-xl leading-relaxed font-medium italic">{aboutContents[0]?.subtitle}</p>
            </div>
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div 
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent" 
                dangerouslySetInnerHTML={{ __html: aboutContents[0]?.description || "" }} 
              />
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{aboutContents.length > 0 ? "Update Content" : "Create Content"}</h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Heading</label>
                  <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all" style={{ '--tw-ring-color': emerald600 }} required disabled={saving} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Subtitle</label>
                  <textarea value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} rows="3" className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all resize-none" style={{ '--tw-ring-color': emerald600 }} required disabled={saving} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Description</label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor key={`jodit-editor-${editorKey}`} ref={editorRef} value={form.description} config={config} onChange={handleEditorChange} />
                  </div>
                </div>
                <button type="submit" style={{ backgroundColor: emerald600 }} className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`} disabled={saving}>
                  {saving ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div> : aboutContents.length > 0 ? 'Update' : 'Create'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        .jodit-editor-container :global(.jodit-container) { border: none !important; background: #f9fafb !important; }
      `}</style>
    </div>
  );
};

export default DLAFellowship;